import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Coins, Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  aiCreditPacksAdminService,
  type AdminAiCreditPack,
  type AiCreditPackPayload,
} from "@/services/aiCreditPacksAdminService";

// Gestion du catalogue des packs de crédits IA. La base est la source de
// vérité : le checkout Stripe (price_data) lit ces valeurs à chaque achat —
// modifier un pack ici est effectif immédiatement, sans toucher à Stripe.
// Les achats passés ne sont jamais affectés (snapshot en metadata + ledger).

const packSchema = Yup.object({
  name: Yup.string().trim().required("Requis").max(100, "100 caractères max"),
  description: Yup.string().nullable(),
  priceEur: Yup.number()
    .typeError("Doit être un nombre")
    .positive("Doit être > 0")
    .max(1000, "Trop élevé")
    .required("Requis"),
  creditsUsd: Yup.number()
    .typeError("Doit être un nombre")
    .positive("Doit être > 0")
    .max(1000, "Trop élevé")
    .required("Requis"),
  displayOrder: Yup.number()
    .typeError("Doit être un nombre")
    .integer("Doit être un entier")
    .min(0, "Doit être >= 0"),
});

interface PackFormValues {
  name: string;
  description: string;
  priceEur: string;
  creditsUsd: string;
  displayOrder: string;
}

const emptyForm: PackFormValues = {
  name: "",
  description: "",
  priceEur: "",
  creditsUsd: "",
  displayOrder: "0",
};

const toFormValues = (pack: AdminAiCreditPack): PackFormValues => ({
  name: pack.name,
  description: pack.description ?? "",
  priceEur: String(pack.priceEur),
  creditsUsd: String(pack.creditsUsd),
  displayOrder: String(pack.displayOrder),
});

const toPayload = (values: PackFormValues): AiCreditPackPayload => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  priceEur: Number(values.priceEur),
  creditsUsd: Number(values.creditsUsd),
  displayOrder: Number(values.displayOrder) || 0,
});

export const AiCreditPacksTab: React.FC = () => {
  const [packs, setPacks] = useState<AdminAiCreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<AdminAiCreditPack | null>(null);
  const [deletingPack, setDeletingPack] = useState<AdminAiCreditPack | null>(
    null,
  );
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Hypothèses du simulateur de rentabilité (réglables par l'admin, non persistées).
  // Change USD→EUR : récupéré en direct (BCE via Frankfurter) puis éditable à la main.
  // Frais Stripe : cartes EEE standard = 1,5 % + 0,25 € (France).
  const [fxRate, setFxRate] = useState("0.90");
  const [fxDate, setFxDate] = useState<string | null>(null);
  const [fxLive, setFxLive] = useState(false);
  const [stripePct, setStripePct] = useState("1.5");
  const [stripeFixed, setStripeFixed] = useState("0.25");

  const refreshFx = useCallback(async () => {
    try {
      const res = await fetch(
        "https://api.frankfurter.dev/v1/latest?base=USD&symbols=EUR",
      );
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as {
        date: string;
        rates: { EUR: number };
      };
      if (typeof data.rates?.EUR === "number") {
        setFxRate(data.rates.EUR.toFixed(4));
        setFxDate(data.date);
        setFxLive(true);
      }
    } catch {
      // Hors-ligne ou API indisponible : on garde la valeur courante (manuelle).
      setFxLive(false);
    }
  }, []);

  useEffect(() => {
    void refreshFx();
  }, [refreshFx]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setPacks(await aiCreditPacksAdminService.list());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const openCreate = () => {
    setEditingPack(null);
    setDialogOpen(true);
  };

  const openEdit = (pack: AdminAiCreditPack) => {
    setEditingPack(pack);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: PackFormValues) => {
    try {
      if (editingPack) {
        await aiCreditPacksAdminService.update(editingPack.id, toPayload(values));
        toast.success("Pack mis à jour");
      } else {
        await aiCreditPacksAdminService.create(toPayload(values));
        toast.success("Pack créé");
      }
      setDialogOpen(false);
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de l'enregistrement");
    }
  };

  const handleToggleActive = async (pack: AdminAiCreditPack) => {
    try {
      setTogglingId(pack.id);
      await aiCreditPacksAdminService.update(pack.id, {
        isActive: !pack.isActive,
      });
      toast.success(pack.isActive ? "Pack désactivé" : "Pack activé");
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de la mise à jour");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deletingPack) return;
    try {
      await aiCreditPacksAdminService.remove(deletingPack.id);
      toast.success("Pack supprimé");
      setDeletingPack(null);
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de la suppression");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" aria-hidden="true" />
              Packs de crédits IA
            </CardTitle>
            <CardDescription>
              Catalogue des packs achetables par les utilisateurs. Le prix (€)
              est encaissé via Stripe ; le budget IA ($) est crédité sur le
              solde de l'utilisateur et consommé après son quota mensuel.
              Les modifications sont effectives immédiatement.
            </CardDescription>
          </div>
          <Button onClick={openCreate} className="shrink-0 gap-1.5 cursor-pointer">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouveau pack
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10" aria-busy="true">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : packs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aucun pack. Créez le premier pack pour ouvrir l'achat de crédits.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Ordre</th>
                    <th className="py-2 pr-4 font-medium">Nom</th>
                    <th className="py-2 pr-4 font-medium">Prix (€)</th>
                    <th className="py-2 pr-4 font-medium">Crédits (€)</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {packs.map((pack) => (
                    <tr key={pack.id} className="border-b last:border-0">
                      <td className="py-3 pr-4 tabular-nums text-muted-foreground">
                        {pack.displayOrder}
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{pack.name}</p>
                        {pack.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[240px]">
                            {pack.description}
                          </p>
                        )}
                      </td>
                      <td className="py-3 pr-4 tabular-nums">
                        {pack.priceEur.toFixed(2)} €
                      </td>
                      <td className="py-3 pr-4 tabular-nums">
                        {pack.creditsUsd.toFixed(2)} €
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={pack.isActive}
                            disabled={togglingId === pack.id}
                            onCheckedChange={() => handleToggleActive(pack)}
                            aria-label={
                              pack.isActive
                                ? `Désactiver ${pack.name}`
                                : `Activer ${pack.name}`
                            }
                          />
                          <Badge variant={pack.isActive ? "default" : "outline"}>
                            {pack.isActive ? "Actif" : "Inactif"}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 cursor-pointer"
                            onClick={() => openEdit(pack)}
                            aria-label={`Modifier ${pack.name}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 cursor-pointer text-destructive hover:text-destructive"
                            onClick={() => setDeletingPack(pack)}
                            aria-label={`Supprimer ${pack.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Simulateur de rentabilité */}
      {!loading && packs.length > 0 && (
        <Card>
          <CardHeader className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" aria-hidden="true" />
              Rentabilité
            </CardTitle>
            <CardDescription>
              Marge brute par pack <strong>si l'utilisateur consomme la totalité
              de son budget IA</strong> (pire cas). Le profit réel est supérieur
              grâce aux crédits non consommés (breakage). Le budget IA est facturé
              en USD par Anthropic — ajustez le taux de change et les frais Stripe.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="fx-rate">Taux de change (USD → EUR)</Label>
                <Input
                  id="fx-rate"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={fxRate}
                  onChange={(e) => {
                    setFxRate(e.target.value);
                    setFxLive(false);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {fxLive && fxDate
                    ? `Taux live BCE du ${new Date(fxDate).toLocaleDateString("fr-FR")}`
                    : "Taux manuel"}
                  {" · "}
                  <button
                    type="button"
                    onClick={() => void refreshFx()}
                    className="underline hover:text-foreground cursor-pointer"
                  >
                    actualiser
                  </button>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="stripe-pct">Frais Stripe (%)</Label>
                <Input
                  id="stripe-pct"
                  type="number"
                  step="0.1"
                  min="0"
                  value={stripePct}
                  onChange={(e) => setStripePct(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="stripe-fixed">Frais Stripe fixe (€)</Label>
                <Input
                  id="stripe-fixed"
                  type="number"
                  step="0.01"
                  min="0"
                  value={stripeFixed}
                  onChange={(e) => setStripeFixed(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Pack</th>
                    <th className="py-2 pr-4 font-medium text-right">Prix encaissé</th>
                    <th className="py-2 pr-4 font-medium text-right">Coût IA max</th>
                    <th className="py-2 pr-4 font-medium text-right">Frais Stripe</th>
                    <th className="py-2 pr-4 font-medium text-right">Marge brute</th>
                    <th className="py-2 font-medium text-right">Marge %</th>
                  </tr>
                </thead>
                <tbody>
                  {packs.map((pack) => {
                    const fx = Number(fxRate) || 0;
                    const pct = Number(stripePct) || 0;
                    const fixed = Number(stripeFixed) || 0;
                    const revenue = pack.priceEur;
                    const costEur = pack.creditsUsd * fx;
                    const fee = revenue * (pct / 100) + fixed;
                    const margin = revenue - costEur - fee;
                    const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
                    const negative = margin < 0;
                    return (
                      <tr key={pack.id} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{pack.name}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {revenue.toFixed(2)} €
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums text-muted-foreground">
                          {costEur.toFixed(2)} €
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums text-muted-foreground">
                          {fee.toFixed(2)} €
                        </td>
                        <td
                          className={`py-3 pr-4 text-right tabular-nums font-semibold ${
                            negative
                              ? "text-destructive"
                              : "text-green-600 dark:text-green-400"
                          }`}
                        >
                          {margin >= 0 ? "+" : ""}
                          {margin.toFixed(2)} €
                        </td>
                        <td
                          className={`py-3 text-right tabular-nums font-semibold ${
                            negative
                              ? "text-destructive"
                              : "text-green-600 dark:text-green-400"
                          }`}
                        >
                          {marginPct >= 0 ? "+" : ""}
                          {marginPct.toFixed(0)} %
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              « Coût IA max » = budget du pack (USD) converti en euros au taux
              saisi. Une marge négative signifie que le pack est vendu à perte si
              le budget est entièrement consommé. Frais Stripe par défaut = cartes
              EEE standard (1,5 % + 0,25 €) ; les cartes premium (1,9 %), UK (2,5 %)
              ou hors-EEE (3,25 % + ~2 % de conversion) coûtent davantage — ajustez
              le pourcentage si votre clientèle est majoritairement hors zone euro.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dialog création / édition */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPack ? "Modifier le pack" : "Nouveau pack"}
            </DialogTitle>
            <DialogDescription>
              Le prix est facturé en euros via Stripe. Les crédits sont
              affichés en euros partout ; en interne, 1 € de crédit couvre
              1 $ de budget IA (facturation Anthropic en USD).
            </DialogDescription>
          </DialogHeader>
          <Formik<PackFormValues>
            initialValues={editingPack ? toFormValues(editingPack) : emptyForm}
            validationSchema={packSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pack-name">Nom</Label>
                  <Field
                    as={Input}
                    id="pack-name"
                    name="name"
                    placeholder="Pack 5 €"
                  />
                  {touched.name && errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pack-description">Description (optionnel)</Label>
                  <Field
                    as={Input}
                    id="pack-description"
                    name="description"
                    placeholder="Idéal pour dépanner en fin de mois"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="pack-price">Prix (€)</Label>
                    <Field
                      as={Input}
                      id="pack-price"
                      name="priceEur"
                      type="number"
                      step="0.01"
                      min="0"
                    />
                    {touched.priceEur && errors.priceEur && (
                      <p className="text-xs text-destructive">{errors.priceEur}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pack-credits">Crédits offerts (€)</Label>
                    <Field
                      as={Input}
                      id="pack-credits"
                      name="creditsUsd"
                      type="number"
                      step="0.01"
                      min="0"
                    />
                    {touched.creditsUsd && errors.creditsUsd && (
                      <p className="text-xs text-destructive">
                        {errors.creditsUsd}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pack-order">Ordre d'affichage</Label>
                  <Field
                    as={Input}
                    id="pack-order"
                    name="displayOrder"
                    type="number"
                    min="0"
                    step="1"
                  />
                  {touched.displayOrder && errors.displayOrder && (
                    <p className="text-xs text-destructive">
                      {errors.displayOrder}
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="cursor-pointer gap-1.5"
                  >
                    {isSubmitting && (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    )}
                    {editingPack ? "Enregistrer" : "Créer"}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation suppression */}
      <Dialog
        open={Boolean(deletingPack)}
        onOpenChange={(open) => !open && setDeletingPack(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer le pack</DialogTitle>
            <DialogDescription>
              Supprimer « {deletingPack?.name} » ? Les achats déjà effectués ne
              sont pas affectés (l'historique conserve le nom du pack). Pour un
              retrait temporaire, préférez la désactivation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              onClick={() => setDeletingPack(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="cursor-pointer"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
